import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {BaseGlowColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-glow-color-option';
import type {RGBA} from '@Utils/color.util';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {GlowEffectOptionsDataArray} from '@Panels/poster-editor-item-effects-panel/components/base-glow-effect-options/base-glow-effect-options.types';
import {getAssetUrl} from '@Utils/s3.util';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {ContextualMenu} from '@Components/contextual-menu';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import styles from './base-glow-effect-options.module.scss';
import {GlowType} from '@PosterWhiteboard/poster/poster-backend.types';

interface BaseGlowEffectOptionsProps {
  panelId: string;
  glowColor: RGBA | null;
  glowType: GlowType;

  onClickColorFill(): void;

  applyGlow(type: GlowType): void;
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/glow_effect/';

export function BaseGlowEffectOptions({...props}: BaseGlowEffectOptionsProps): ReactElement | null {
  useEffect((): void => {
    updateHeightInRedux(props.glowType);
  }, [props.glowType]);

  const updateHeightInRedux = (effect: GlowType): void => {
    let height = '182px';

    if (effect === GlowType.LIGHT || effect === GlowType.STRONG) {
      height = '228px';
    }

    updateOptionsPanelHeight(props.panelId, height);
  };

  const getColorOption = (): ReactElement | null => {
    if (!props.glowColor) {
      return null;
    }

    return (
      <BaseGlowColorOption
        glowColor={props.glowColor}
        onClickColorFill={(): void => {
          props.onClickColorFill();
        }}
        className="spacing-m-b-3"
        glowType={props.glowType}
      />
    );
  };

  const getTextForEffect = (glowColor: GlowType): string => {
    switch (glowColor) {
      case GlowType.LIGHT:
        return window.i18next.t('pmwjs_light_glow');
      case GlowType.STRONG:
        return window.i18next.t('pmwjs_strong_glow');
      default:
        return window.i18next.t('pmwjs_none');
    }
  };

  const getThumbnails = (): ReactElement[] => {
    return GlowEffectOptionsDataArray.map((glowEffect) => {
      return (
        <ContextualMenuThumbnailItem
          key={glowEffect.effect}
          className={styles.thumbnailsClasses}
          thumbnailContainerClassName={styles.largeThumbnail}
          thumbnail={
            <Thumbnail
              className={glowEffect.effect !== GlowType.NONE ? '' : styles.thumbnail}
              data={{
                type: ThumbnailType.IMAGE,
                imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}${glowEffect.thumbnail}.${getCompatibleImageFileExtension('webp')}`),
              }}
            />
          }
          hasBorder={false}
          isSelected={props.glowType === glowEffect.effect}
          selectionState={ThumbnailItemSelectionState.OUTLINE}
          text={getTextForEffect(glowEffect.effect)}
          textClassName="content-body spacing-m-t-1"
          onClick={(): void => {
            props.applyGlow(glowEffect.effect);
          }}
        />
      );
    });
  };

  return (
    <ContextualMenu id="glow-mobile-options" hasMobileMorphingProperties hasMobileModalPadding>
      {getColorOption()}
      <ul className={`flex-h-row spacing-m-0 ${styles.list}`}>{getThumbnails()}</ul>
    </ContextualMenu>
  );
}
